import Breadcrumb from "@/components/feature/Breadcrumb";
import FeedbackForm from "./components/FeedbackForm";
import { usePageSeo } from "@/hooks/usePageSeo";

const services = [
  {
    icon: "ri-play-list-line",
    title: "影视内容浏览",
    desc: "聚合电影、电视剧、综艺等多元影视类型，提供统一的封面、简介与资料浏览体验。",
  },
  {
    icon: "ri-equalizer-line",
    title: "分类与筛选",
    desc: "支持按地区、年份、语言、题材等多维度筛选，帮助你快速定位想看的作品。",
  },
  {
    icon: "ri-bookmark-3-line",
    title: "专题合集",
    desc: "围绕题材与影人整理精选专题，把零散的作品串成一条完整的观影线索。",
  },
  {
    icon: "ri-bookmark-line",
    title: "收藏与标记",
    desc: "收藏功能基于浏览器本地存储，用于演示收藏与列表标记效果，不会上传任何个人数据。",
  },
];

const faqs = [
  {
    q: "站内的影视作品是真实存在的吗？",
    a: "本站为内容演示站点，影片名称、简介、影人资料均为虚构内容，用于展示影视门户的信息结构与浏览体验。",
  },
  {
    q: "为什么点击播放按钮没有反应？",
    a: "本站暂未接入真实播放接口，播放相关按钮均为演示状态，点击后会提示暂未接入播放功能。",
  },
  {
    q: "收藏的数据保存在哪里？",
    a: "收藏仅保存在你浏览器的本地存储中，不会上传到服务器，清除浏览器数据后收藏记录会随之消失。",
  },
];

export default function AboutPage() {
  usePageSeo({
    title: "关于我们 - 91av视频综合影视视频资源平台",
    description:
      "了解 91av视频的网站宗旨、服务范围与联系方式。我们为影视爱好者提供电影、电视剧、综艺等内容浏览与筛选体验。",
    keywords: "91av视频,关于我们,联系我们,影视资源平台,用户反馈",
  });

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      <section className="mt-5 overflow-hidden rounded-lg border border-background-200/70 bg-primary-950">
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="p-8 md:p-12">
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
              <i className="ri-information-line" />
              关于 91av视频
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-4xl">
              让每一次找片都更简单
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-primary-100 md:text-base">
              91av视频是面向广大影视爱好者的综合影视视频资源平台。我们的宗旨是，用清晰的内容分类、
              便捷的筛选工具与完整的影片资料，帮助每一位用户在丰富多样的影视内容中，快速找到
              此刻最想看的那一部作品。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-primary-200">
              网站目前提供内容浏览与信息查询服务，涵盖电影、电视剧、综艺等类型，并持续整理主题专题
              与相关推荐，让浏览与发现的过程更有乐趣。
            </p>
          </div>
          <div className="relative min-h-[240px]">
            <img
              src="https://readdy.ai/api/search-image?query=a%20warm%20modern%20home%20cinema%20corner%20with%20a%20large%20screen%20glowing%2C%20soft%20ambient%20light%2C%20comfortable%20sofa%20and%20snacks%2C%20cinematic%20atmosphere%2C%20highly%20detailed%20photography&width=800&height=700&seq=cnfilm-about-hero&orientation=landscape"
              alt="舒适的家庭观影场景"
              title="91av视频 - 综合影视视频资源在线观看平台"
              className="h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/40 to-transparent md:bg-gradient-to-r md:from-primary-950/90 md:to-transparent" />
          </div>
        </div>
      </section>

      <section className="mt-10">
        <div className="mb-5 flex items-center gap-2">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
            <i className="ri-service-line text-xl" />
          </span>
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">服务范围</h2>
            <p className="text-xs text-foreground-500 md:text-sm">我们为影视爱好者提供这些内容服务</p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((item) => (
            <div
              key={item.title}
              className="rounded-lg border border-background-200/70 bg-background-50 p-5"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                <i className={`${item.icon} text-xl`} />
              </span>
              <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <div className="mb-5 flex items-center gap-2">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className="ri-customer-service-2-line text-xl" />
            </span>
            <div>
              <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">联系我们</h2>
              <p className="text-xs text-foreground-500 md:text-sm">欢迎通过以下方式与我们取得联系</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-3 rounded-lg border border-background-200/70 bg-background-50 p-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-background-100 text-primary-700">
                <i className="ri-mail-line text-lg" />
              </span>
              <div>
                <p className="text-xs text-foreground-500">合作与反馈邮箱</p>
                <p className="text-sm font-medium text-foreground-900">service@91av-video.example</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-background-200/70 bg-background-50 p-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-background-100 text-primary-700">
                <i className="ri-time-line text-lg" />
              </span>
              <div>
                <p className="text-xs text-foreground-500">服务时间</p>
                <p className="text-sm font-medium text-foreground-900">周一至周五 09:00 - 18:00</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-secondary-200 bg-secondary-100/70 p-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-background-50 text-secondary-700">
                <i className="ri-lightbulb-line text-lg" />
              </span>
              <p className="text-sm leading-relaxed text-secondary-900">
                若你发现影片信息有误，或对网站功能有新的想法，欢迎通过右侧反馈表单告诉我们，
                你的每一条建议都很珍贵。
              </p>
            </div>
          </div>
        </div>

        <FeedbackForm />
      </section>

      <section className="mt-10">
        <div className="mb-5 flex items-center gap-2">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
            <i className="ri-question-line text-xl" />
          </span>
          <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">常见问题</h2>
        </div>
        <div className="space-y-3">
          {faqs.map((faq) => (
            <div
              key={faq.q}
              className="rounded-lg border border-background-200/70 bg-background-50 p-5"
            >
              <h3 className="flex items-start gap-2 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-record-circle-line mt-1 text-accent-500" />
                {faq.q}
              </h3>
              <p className="mt-2 pl-6 text-sm leading-relaxed text-foreground-600">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}